import type { CardId, EventId, RunState } from "../domain/types/index.js";

export type MapEventEffect =
  | { kind: "fatigue"; amount: number }
  | { kind: "addCard"; cardId: CardId }
  | { kind: "addRandomAction"; tier: "small" | "medium" | "large" }
  | { kind: "upgradeCard"; from: CardId; to: CardId }
  | { kind: "nextCase"; stat: "deadline" | "required" | "energy"; amount: number };

export type MapEventDefinition = {
  id: EventId;
  name: string;
  flavor: string;
  effects: readonly MapEventEffect[];
};

export const MAP_EVENTS = [
  {
    id: "experienced-hire",
    name: "経験者採用",
    flavor: "即戦力になりそうな人物がいる。ただし、受け入れ準備には手間がかかる。",
    effects: [{ kind: "addCard", cardId: "mid" }, { kind: "fatigue", amount: 1 }],
  },
  {
    id: "large-hire",
    name: "大型採用",
    flavor: "ベテランを迎えられる。ただし、別部署から一人引き取ることが条件だ。",
    effects: [
      { kind: "addCard", cardId: "senior" },
      { kind: "addCard", cardId: "idle" },
      { kind: "fatigue", amount: 2 },
    ],
  },
  {
    id: "improvement-proposal",
    name: "業務改善案",
    flavor: "現場から改善案が上がった。小さく試せる施策を導入する。",
    effects: [{ kind: "addRandomAction", tier: "small" }],
  },
  {
    id: "emergency-budget",
    name: "臨時予算",
    flavor: "追加予算が下りた。導入作業まで今日中に終わらせれば使える。",
    effects: [{ kind: "addRandomAction", tier: "medium" }, { kind: "fatigue", amount: 2 }],
  },
  {
    id: "executive-project",
    name: "経営直轄プロジェクト",
    flavor: "役員直轄で強力な施策を導入できる。ただし、余剰人員も引き取り、現場には負担がかかる。",
    effects: [
      { kind: "addRandomAction", tier: "large" },
      { kind: "addCard", cardId: "idle" },
      { kind: "fatigue", amount: 2 },
    ],
  },
  {
    id: "deadline-negotiation",
    name: "納期交渉",
    flavor: "交渉の席を用意できる。納期に余裕が生まれる。",
    effects: [{ kind: "nextCase", stat: "deadline", amount: 2 }],
  },
  {
    id: "crash-team",
    name: "突貫体制",
    flavor: "次のプロジェクトに人員を先行投入できる。開始時から施策を回しやすくなる。",
    effects: [{ kind: "nextCase", stat: "energy", amount: 1 }],
  },
  {
    id: "internal-training",
    name: "特別研修",
    flavor: "選抜研修で、新卒を即戦力へ育成できる。ただし研修中は、次のプロジェクトに十分な手を回せない。",
    effects: [
      { kind: "upgradeCard", from: "intern", to: "mid" },
      { kind: "nextCase", stat: "energy", amount: -1 },
    ],
  },
  {
    id: "special-leave",
    name: "特別休暇",
    flavor: "全員を休ませ、疲労を大きく回復できる。",
    effects: [{ kind: "fatigue", amount: -3 }],
  },
  {
    id: "external-consultant",
    name: "外部コンサル導入",
    flavor: "二つの施策を持ち込んでくれるが、余剰人員も引き取る契約になっている。",
    effects: [
      { kind: "addRandomAction", tier: "small" },
      { kind: "addRandomAction", tier: "medium" },
      { kind: "addCard", cardId: "idle" },
    ],
  },
] as const satisfies readonly MapEventDefinition[];

export function getMapEvent(eventId: EventId): MapEventDefinition {
  const event = MAP_EVENTS.find((candidate) => candidate.id === eventId);
  if (!event) throw new Error(`Unknown map event: ${eventId}`);
  return event;
}

function hasCard(run: RunState, cardId: CardId): boolean {
  return [run.deck, run.discard, run.hand, run.inPlay, run.aside]
    .flat()
    .some((card) => card.cardId === cardId);
}

/** 新卒がいないランでは特別研修を提示しない。 */
export function availableMapEvents(run: RunState): readonly MapEventDefinition[] {
  return MAP_EVENTS.filter((event) => event.id !== "internal-training" || hasCard(run, "intern"));
}
